import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  CircularProgress,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Print as PrintIcon,
  RotateRight as RotateRightIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
} from '@mui/icons-material';
import { Document, Page, pdfjs, type DocumentProps } from 'react-pdf';
import PdfWorker from 'pdfjs-dist/build/pdf.worker.min.mjs?worker';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

pdfjs.GlobalWorkerOptions.workerPort = new PdfWorker();

type PdfPageViewerProps = {
  file: DocumentProps['file'];
  height?: number | string;
  width?: number | string;
  id?: string;
  maxPageWidth?: number;
};

const VIEWER_PADDING = 32;
const TOOLBAR_HEIGHT = 48;
const MIN_PAGE_WIDTH = 280;
const MIN_ZOOM = 0.5;
const MAX_ZOOM = 2.5;
const ZOOM_STEP = 0.25;
const ROTATION_STEP = 90;

function PdfPageViewer({
  file,
  height = '90vh',
  width = '100%',
  id,
  maxPageWidth = 1100,
}: PdfPageViewerProps) {
  const viewerRef = useRef<HTMLDivElement | null>(null);
  const documentScrollerRef = useRef<HTMLDivElement | null>(null);
  const pageContainerRefs = useRef<Record<number, HTMLDivElement | null>>({});
  const printFrameRef = useRef<HTMLIFrameElement | null>(null);
  const printObjectUrlRef = useRef<string | null>(null);
  const [viewerWidth, setViewerWidth] = useState(0);
  const [numPages, setNumPages] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageInput, setPageInput] = useState('1');
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [loadError, setLoadError] = useState('');
  const [printError, setPrintError] = useState('');
  const [printing, setPrinting] = useState(false);

  const fileSource = useMemo(() => file, [file]);

  useEffect(() => {
    setNumPages(0);
    setPageNumber(1);
    setPageInput('1');
    setZoom(1);
    setRotation(0);
    setLoadError('');
    setPrintError('');
  }, [fileSource]);

  const cleanupPrintResources = useCallback(() => {
    if (printFrameRef.current) {
      printFrameRef.current.remove();
      printFrameRef.current = null;
    }

    if (printObjectUrlRef.current) {
      URL.revokeObjectURL(printObjectUrlRef.current);
      printObjectUrlRef.current = null;
    }
  }, []);

  useEffect(() => {
    const viewer = viewerRef.current;

    if (!viewer || typeof ResizeObserver === 'undefined') {
      return undefined;
    }

    const resizeObserver = new ResizeObserver(([entry]) => {
      setViewerWidth(entry.contentRect.width);
    });

    resizeObserver.observe(viewer);

    return () => resizeObserver.disconnect();
  }, []);

  useEffect(() => cleanupPrintResources, [cleanupPrintResources]);

  useEffect(() => {
    const documentScroller = documentScrollerRef.current;
    const pageContainer = pageContainerRefs.current[pageNumber];

    if (!numPages || !documentScroller || !pageContainer) {
      return;
    }

    const scrollerRect = documentScroller.getBoundingClientRect();
    const pageRect = pageContainer.getBoundingClientRect();
    const nextScrollTop =
      documentScroller.scrollTop + pageRect.top - scrollerRect.top;

    documentScroller.scrollTo({
      top: nextScrollTop,
    });
  }, [numPages, pageNumber]);

  const setPageContainerRef = useCallback(
    (nextPageNumber: number) => (element: HTMLDivElement | null) => {
      pageContainerRefs.current[nextPageNumber] = element;
    },
    [],
  );

  const createPrintObjectUrl = useCallback(
    (data: BlobPart | BlobPart[]) => {
      cleanupPrintResources();

      const blobParts = Array.isArray(data) ? data : [data];
      const objectUrl = URL.createObjectURL(
        new Blob(blobParts, { type: 'application/pdf' }),
      );
      printObjectUrlRef.current = objectUrl;

      return objectUrl;
    },
    [cleanupPrintResources],
  );

  const getPrintUrl = useCallback(() => {
    if (!fileSource) {
      return '';
    }

    if (typeof fileSource === 'string') {
      return fileSource;
    }

    if (fileSource instanceof Blob) {
      return createPrintObjectUrl(fileSource);
    }

    if (fileSource instanceof ArrayBuffer) {
      return createPrintObjectUrl(fileSource);
    }

    if ('url' in fileSource && fileSource.url) {
      return fileSource.url;
    }

    if ('data' in fileSource && fileSource.data) {
      const data = fileSource.data;

      if (Array.isArray(data)) {
        return createPrintObjectUrl(new Uint8Array(data));
      }

      return createPrintObjectUrl(data);
    }

    return '';
  }, [createPrintObjectUrl, fileSource]);

  const pageWidth = useMemo(() => {
    const availableWidth = Math.max(viewerWidth - VIEWER_PADDING, 0);

    if (!availableWidth) {
      return undefined;
    }

    const fitWidth = Math.min(availableWidth, maxPageWidth);

    return Math.max(MIN_PAGE_WIDTH, fitWidth * zoom);
  }, [maxPageWidth, viewerWidth, zoom]);

  const pageNumbers = useMemo(
    () => Array.from({ length: numPages }, (_, pageIndex) => pageIndex + 1),
    [numPages],
  );

  const updatePage = (nextPage: number) => {
    if (!numPages) {
      return;
    }

    const clampedPage = Math.min(Math.max(nextPage, 1), numPages);
    setPageNumber(clampedPage);
    setPageInput(String(clampedPage));
  };

  const handlePageInputBlur = () => {
    const nextPage = Number(pageInput);

    if (!Number.isInteger(nextPage)) {
      setPageInput(String(pageNumber));
      return;
    }

    updatePage(nextPage);
  };

  const updateZoom = (nextZoom: number) => {
    setZoom(Math.min(Math.max(nextZoom, MIN_ZOOM), MAX_ZOOM));
  };

  const rotateClockwise = () => {
    setRotation((currentRotation) => (currentRotation + ROTATION_STEP) % 360);
  };

  const finishPrint = useCallback(() => {
    setPrinting(false);
    window.setTimeout(cleanupPrintResources, 1000);
  }, [cleanupPrintResources]);

  const handlePrint = () => {
    const printUrl = getPrintUrl();

    if (!printUrl) {
      setPrintError('Unable to prepare PDF for printing.');
      return;
    }

    setPrintError('');
    setPrinting(true);

    const iframe = document.createElement('iframe');
    printFrameRef.current = iframe;
    iframe.title = 'PDF print frame';
    iframe.style.border = '0';
    iframe.style.height = '1px';
    iframe.style.opacity = '0';
    iframe.style.position = 'fixed';
    iframe.style.right = '0';
    iframe.style.top = '0';
    iframe.style.width = '1px';

    iframe.onload = () => {
      try {
        const printWindow = iframe.contentWindow;

        if (!printWindow) {
          throw new Error('Missing print window');
        }

        printWindow.focus();
        printWindow.addEventListener('afterprint', finishPrint, {
          once: true,
        });
        printWindow.print();
        window.setTimeout(finishPrint, 120000);
      } catch (error) {
        finishPrint();
        const printWindow = window.open(printUrl, '_blank');

        if (!printWindow) {
          setPrintError('Unable to open PDF for printing.');
        }
      }
    };

    iframe.onerror = () => {
      finishPrint();
      setPrintError('Unable to open PDF for printing.');
    };

    iframe.src = printUrl;
    document.body.appendChild(iframe);
  };

  const loadingContent = (
    <Box
      alignItems="center"
      display="flex"
      gap="12px"
      justifyContent="center"
      minHeight="240px"
    >
      <CircularProgress size={24} />
      <Typography>Loading PDF...</Typography>
    </Box>
  );

  return (
    <Box
      id={id}
      ref={viewerRef}
      display="flex"
      flexDirection="column"
      height={height}
      minHeight="360px"
      overflow="visible"
      width={width}
      bgcolor="#f4f6f8"
      sx={{ isolation: 'isolate' }}
    >
      <Box
        alignItems="center"
        bgcolor="#fff"
        borderBottom="1px solid rgba(0, 0, 0, 0.12)"
        display="flex"
        flex="0 0 auto"
        flexWrap="wrap"
        gap="8px"
        minHeight={`${TOOLBAR_HEIGHT}px`}
        justifyContent="center"
        position="sticky"
        top={0}
        zIndex={10}
      >
        <Tooltip title="Previous page">
          <span>
            <IconButton
              aria-label="Previous page"
              disabled={pageNumber <= 1}
              onClick={() => updatePage(pageNumber - 1)}
              size="small"
            >
              <ChevronLeftIcon />
            </IconButton>
          </span>
        </Tooltip>
        <TextField
          aria-label="Page number"
          disabled={!numPages}
          inputProps={{
            min: 1,
            max: numPages || 1,
            style: {
              textAlign: 'center',
            },
          }}
          onBlur={handlePageInputBlur}
          onChange={(event) => setPageInput(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              handlePageInputBlur();
            }
          }}
          size="small"
          type="number"
          value={pageInput}
        />
        <Typography
          color="textSecondary"
          variant="body2"
        >
          of {numPages || '-'}
        </Typography>
        <Tooltip title="Next page">
          <span>
            <IconButton
              aria-label="Next page"
              disabled={!numPages || pageNumber >= numPages}
              onClick={() => updatePage(pageNumber + 1)}
              size="small"
            >
              <ChevronRightIcon />
            </IconButton>
          </span>
        </Tooltip>
        <Box
          alignItems="center"
          display="flex"
          gap="4px"
        >
          <Tooltip title="Zoom out">
            <span>
              <IconButton
                aria-label="Zoom out"
                disabled={zoom <= MIN_ZOOM}
                onClick={() => updateZoom(zoom - ZOOM_STEP)}
                size="small"
              >
                <ZoomOutIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Typography
            minWidth="48px"
            textAlign="center"
            variant="body2"
          >
            {Math.round(zoom * 100)}%
          </Typography>
          <Tooltip title="Zoom in">
            <span>
              <IconButton
                aria-label="Zoom in"
                disabled={zoom >= MAX_ZOOM}
                onClick={() => updateZoom(zoom + ZOOM_STEP)}
                size="small"
              >
                <ZoomInIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
        <Tooltip title="Print">
          <span>
            <IconButton
              aria-label="Print"
              disabled={!fileSource || printing}
              onClick={handlePrint}
              size="small"
            >
              {printing ? <CircularProgress size={20} /> : <PrintIcon />}
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Rotate clockwise">
          <span>
            <IconButton
              aria-label="Rotate clockwise"
              disabled={!numPages}
              onClick={rotateClockwise}
              size="small"
            >
              <RotateRightIcon />
            </IconButton>
          </span>
        </Tooltip>
        {printError && (
          <Typography
            color="error"
            variant="caption"
          >
            {printError}
          </Typography>
        )}
      </Box>
      <Box
        ref={documentScrollerRef}
        display="block"
        flex="1 1 auto"
        minHeight={0}
        overflow="auto"
        p="16px"
        sx={{
          '& .pdf-document': {
            alignItems: 'center',
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
            margin: '0 auto',
            minWidth: 'fit-content',
          },
        }}
      >
        {loadError ? (
          <Typography color="error">{loadError}</Typography>
        ) : (
          <Document
            className="pdf-document"
            error={<Typography color="error">Unable to load PDF.</Typography>}
            file={fileSource}
            loading={loadingContent}
            onLoadError={(error) => setLoadError(error.message)}
            onLoadSuccess={({ numPages: nextNumPages }) => {
              setNumPages(nextNumPages);
              setPageInput('1');
              setPageNumber(1);
            }}
          >
            {pageNumbers.map((nextPageNumber) => (
              <Box
                key={nextPageNumber}
                ref={setPageContainerRef(nextPageNumber)}
              >
                <Page
                  loading={loadingContent}
                  pageNumber={nextPageNumber}
                  rotate={rotation}
                  width={pageWidth}
                />
              </Box>
            ))}
          </Document>
        )}
      </Box>
    </Box>
  );
}

export default PdfPageViewer;
